import _ from 'lodash';
import { getFeaturesConfig } from 'src/models/lmsConfig/queries';

export const EMPTY_PATH = '/';
export const HOMEPAGE_PATH = '/homepage';
export const HOMEPAGE_KEY = 'home';

export const MENU_GROUP_TYPES = {
  WORKSPACE: 'WORKSPACE',
  DISTRIBUTION: 'DISTRIBUTION',
  SALES: 'SALES',
  COLLECTIONS: 'COLLECTIONS',
  UTILITY: 'UTILITY',
  MORE: 'MORE',
};

// Module codes
export const MODULE_CODES = {
  SETTINGS: 'settings',
  OTHERS: 'others',
  REPORTS: 'reports',
  TABLEAU: 'tableau',
  SUPERSET_DASHBOARD: 'superset-dashboard',
  CUSTOMIZE: 'customize',
  APPROVAL: 'approval',
  ONBOARD: 'onboard',
  WAIVER_MATRIX: 'waiver_matrix',
  GOAL_MANAGEMENT: 'goal_management',
  HOME: 'home',
  TASKS: 'tasks',
  COMMON: 'common',
  MASTERS: 'masters',
  WFH: 'wfh',
  RESOURCE_CENTER: 'resource_center',
  CLIENT_HEALTH_DASHBOARD: 'CLIENT_HEALTH_DASHBOARD',
  PERFORMANCE: 'performance',
} as const;

// Module paths
export const MODULE_PATHS = {
  CAMPAIGN_BO_LIST: '/web2/campaign/bo-list',
  GOAL_MANAGEMENT_BULK_UPLOAD: '/web2/goal_management/bulk-upload',
  SUPERSET_DETAILS: '/supersetDetails',
  APPROVALS: '/web2/approvals',
  WAIVER_MATRIX: '/web2/waiver-matrix',
  WFH: '/web2/wfh',
  MASTERS: '/web2/masters',
  TASKS: '/web2/tasks',
  SETTINGS: '/web2/settings',
  COMMON: '/web2/common',
  OTHERS: '/web2/others',
  REPORTS_ALL: '/web2/reports/all',
  TABLEAU: '/web2/tableau',
  RESOURCE_CENTER: '/web2/resource-center',
  CUSTOMIZE: '/web2/customize',
  ONBOARD: '/web2/onboard',
  SUPERSET_DASHBOARD: '/web2/dashboards',
  CLIENT_HEALTH_DASHBOARD: '/web2/client-health-dashboard',
} as const;

// Revyre paths
export const REVYRE_PATHS = {
  WORKFLOW_PREFIX: 'revyre/workflow/',
  ACTIVITY: 'revyre/activity',
  PREFIX: 'revyre/',
  DDP: '/workflow-ddp-status/list',
} as const;

// Section types
export const SECTION_TYPES = {
  MANAGE_GROUPS: 'manage-groups',
  MANAGE_USERS: 'manage-users',
  BO_LIST: 'bo-list',
  ACTIVITY_BULK_UPLOAD: 'activity-bulk-upload',
  GOAL_BULK_UPLOAD: 'goal-bulk-upload',
  HOME_PAGE: 'home-page',
  MASTERS: 'masters',
  ACTIVITIES_BULK_UPLOAD: 'ACTIVITIES_BULK_UPLOAD',
  CLIENT_HEALTH_DASHBOARD: 'client-health-dashboard',
  WFH_BOARD: 'wfh-board',
  WORKFLOW_DOWNLOADS: 'workflow-ddp-status',
  PERFORMANCE_DASHBOARD: 'performance-dashboard',
  PERFORMANCE_LEADERBOARD: 'performance-leaderboard',
} as const;

// Type suffixes and prefixes
export const TYPE_SUFFIXES = {
  DETAILS: 'details',
} as const;

export const TYPE_PREFIXES = {
  LEADS: 'leads',
  JOB_DETAILS: 'job-details',
  TASK_DETAILS: 'taskDetails',
} as const;

// Section names
export const SECTION_NAMES = {
  MANAGE_GROUPS: 'Manage groups',
  GOALS_BULK_UPLOAD: 'Goals Bulk Upload',
  HOME: 'Home',
  MASTERS_LIST: 'Masters List',
  BULK_UPLOAD: 'Bulk Upload',
  CLIENT_HEALTH_DASHBOARD: 'Client Health Dashboard',
  PERFORMANCE_DASHBOARD: 'Dashboard',
  PERFORMANCE_LEADERBOARD: 'Leaderboard',
} as const;

// Type strings
export const TYPE_STRINGS = {
  REVYRE: 'revyre',
  WORKFLOW: 'WORKFLOW',
  ACTIVITY: 'ACTIVITY',
  HIERARCHY: 'HIERARCHY',
  WORKFLOW_DOWNLOADS: 'Workflow Downloads',
} as const;

// Other constants
export const REVYRE_CONSTANTS = {
  ACTIVITY_CODE: 'revyreActivity',
  ACTIVITY_NAME: 'Revyre Activities',
  KEY_PREFIX: 'revyre_',
} as const;

export const USER_CONSTANTS = {
  SUPER_USER_CODE: 'SU',
} as const;

export const CLIENT_CONSTANTS = {
  FEC_CLIENT_CODE: 'fec',
} as const;

export const ROLE_CONSTANTS = {
  ALLOWED_GOALS_ROLES: ['Collx Admin', 'Collx Manager'],
} as const;

// Revyre Activity Module Structure
export const getRevyreActivityModule: any = () => ({
  name: REVYRE_CONSTANTS.ACTIVITY_NAME,
  key: REVYRE_CONSTANTS.ACTIVITY_CODE,
  code: REVYRE_CONSTANTS.ACTIVITY_CODE,
  type: TYPE_STRINGS.ACTIVITY,
  isRevyre: true,
  children: [
    {
      name: REVYRE_CONSTANTS.ACTIVITY_NAME,
      code: REVYRE_CONSTANTS.ACTIVITY_CODE,
      icon: '',
      display: true,
      type: TYPE_STRINGS.REVYRE,
      path: REVYRE_PATHS.ACTIVITY,
      key: `revyre_activity`,
    },
  ],
});

export const workflowDownloadSection = {
  name: TYPE_STRINGS.WORKFLOW_DOWNLOADS,
  code: SECTION_TYPES.WORKFLOW_DOWNLOADS,
  key: SECTION_TYPES.WORKFLOW_DOWNLOADS,
  icon: '',
  display: true,
  modules: [MODULE_CODES.OTHERS],
  path: REVYRE_PATHS.DDP,
};

// Helper functions to create section objects
export const createSection = (
  type: string,
  name: string,
  modules: string[],
  group?: string,
) => ({
  type,
  name,
  display: true,
  modules,
  group,
});

// Predefined section configurations
export const SECTION_CONFIGS = {
  GOAL_BULK_UPLOAD: () =>
    createSection(
      SECTION_TYPES.GOAL_BULK_UPLOAD,
      SECTION_NAMES.GOALS_BULK_UPLOAD,
      [MODULE_CODES.GOAL_MANAGEMENT],
      MENU_GROUP_TYPES.WORKSPACE,
    ),
  HOME_PAGE: () =>
    createSection(SECTION_TYPES.HOME_PAGE, SECTION_NAMES.HOME, [
      MODULE_CODES.HOME,
      MENU_GROUP_TYPES.WORKSPACE,
    ]),
  MASTERS: () =>
    createSection(SECTION_TYPES.MASTERS, SECTION_NAMES.MASTERS_LIST, [
      MODULE_CODES.MASTERS,
      MENU_GROUP_TYPES.DISTRIBUTION,
    ]),
  ACTIVITIES_BULK_UPLOAD: () =>
    createSection(
      SECTION_TYPES.ACTIVITIES_BULK_UPLOAD,
      SECTION_NAMES.BULK_UPLOAD,
      [MODULE_CODES.TASKS],
      MENU_GROUP_TYPES.WORKSPACE,
    ),
  CLIENT_HEALTH_DASHBOARD: () =>
    createSection(
      SECTION_TYPES.CLIENT_HEALTH_DASHBOARD,
      SECTION_NAMES.CLIENT_HEALTH_DASHBOARD,
      [MODULE_CODES.CLIENT_HEALTH_DASHBOARD],
      MENU_GROUP_TYPES.MORE,
    ),
  PERFORMANCE_DASHBOARD: () =>
    createSection(
      SECTION_TYPES.PERFORMANCE_DASHBOARD,
      SECTION_NAMES.PERFORMANCE_DASHBOARD,
      [MODULE_CODES.PERFORMANCE],
      MENU_GROUP_TYPES.WORKSPACE,
    ),
  PERFORMANCE_LEADERBOARD: () =>
    createSection(
      SECTION_TYPES.PERFORMANCE_LEADERBOARD,
      SECTION_NAMES.PERFORMANCE_LEADERBOARD,
      [MODULE_CODES.PERFORMANCE],
      MENU_GROUP_TYPES.WORKSPACE,
    ),
} as const;

// TODO: i18n and check geo and adoption here
export const getUIModules = () => [
  {
    code: MODULE_CODES.SETTINGS,
    name: _.get(getFeaturesConfig(), 'broadcast_messages_enabled', false)
      ? 'Manage'
      : 'Settings',
    type: 'settings',
  },
  { code: MODULE_CODES.COMMON, name: 'Geo and Adoptions', type: 'common' },
  {
    code: MODULE_CODES.PERFORMANCE,
    name: 'Performance',
    type: 'performance',
  },

  { code: MODULE_CODES.REPORTS, name: 'Reports', type: 'reports' },
  { code: MODULE_CODES.TABLEAU, name: 'Tableau', type: 'tableau' },
  { code: MODULE_CODES.CUSTOMIZE, name: 'Customize', type: 'customize' },
  { code: MODULE_CODES.TASKS, name: 'Activities', type: 'tasks' },
  {
    code: MODULE_CODES.GOAL_MANAGEMENT,
    name: 'Goal Management',
    type: 'goal_management',
  },
  { code: MODULE_CODES.WFH, name: 'WFH', type: 'wfh' },
  { code: MODULE_CODES.ONBOARD, name: 'On boarding', type: 'onboard' },
  {
    code: MODULE_CODES.RESOURCE_CENTER,
    name: 'Resource Center',
    type: 'resource_center',
  },
  { code: MODULE_CODES.HOME, name: 'Home', type: 'home' },
  { code: MODULE_CODES.APPROVAL, name: 'Approvals', type: 'approval' },
  { code: MODULE_CODES.OTHERS, name: 'Others', type: 'others' },
  { code: MODULE_CODES.MASTERS, name: 'Masters List', type: 'masters' },
  {
    code: MODULE_CODES.CLIENT_HEALTH_DASHBOARD,
    name: 'Client Health Dashboard',
    type: 'client_health_dashboard',
  },
  {
    code: MODULE_CODES.WAIVER_MATRIX,
    name: 'Approval Matrix',
    type: 'waiver_matrix',
  },
  {
    code: MODULE_CODES.SUPERSET_DASHBOARD,
    name: 'Superset Dashboard',
    type: 'superset_dashboard',
  },
];

export const routesWithPath = {
  logout: {
    path: '/logout',
    displayName: 'logout',
  },
  masters: {
    path: '/masters',
    displayName: 'Masters List',
  },
  'manage-users': {
    path: '/manage-users',
    displayName: 'Manage users',
  },
  productivity: {
    path: '/:module/productivity',
    displayName: 'Productivity',
  },
  funnel: {
    path: '/:module/funnel',
    displayName: 'Funnel',
  },
  geo: {
    path: '/:module/geo',
    displayName: 'Geo',
  },
  businesscharts: {
    path: '/trends/business-charts',
    displayName: 'Partner Business Charts',
  },
  cumulative: {
    path: '/:module/cumulative',
    displayName: 'Cumulative',
  },
  'activity-metrics': {
    path: '/:module/activity-metrics',
    displayName: 'Activity Metrics',
  },
  'activity-funnel-metrics': {
    path: '/:module/activity-funnel-metrics',
    displayName: 'Activity Funnel Metrics',
  },
  'activity-funnel-metrics.showv2': {
    path: '/:module/activity-funnel-metrics.showv2',
    displayName: 'Activity Funnel Metrics V2',
  },
  reports: {
    path: '/:module/all',
    displayName: 'Reports',
  },
  report: {
    path: '/:module/report',
    displayName: 'Report View',
  },
  allocations: {
    path: '/:module/allocations',
    displayName: 'Allocations',
  },
  'manage-user-attributes': {
    path: '/:module/manage-user-attributes',
    displayName: 'Manage User Attributes',
  },
  'manage-system-roles': {
    path: '/:module/manage-system-roles',
    displayName: 'Manage System Roles',
  },
  'push-status': {
    path: '/:module/push-status',
    displayName: 'Push Status',
  },
  verify_user: {
    path: '/verify',
    displayName: 'User verification',
  },
  'crm-updates-status': {
    path: '/:module/crm-updates-status',
    displayName: 'CRM Update Status',
  },
  'travel-summary': {
    path: '/:module/travel-summary',
    displayName: 'Travel Summary',
  },
  'standard-reports': {
    path: '/:module/standard-reports',
    displayName: 'Standard Reports',
  },
  targets: {
    path: '/targets',
    displayName: 'Targets',
  },
  'targets-upload': {
    path: '/:module/targets-upload',
    displayName: 'Target Reports',
  },
  'ddp-status': {
    path: '/ddp-status',
    displayName: 'Downloads',
  },
  'ddp-dataout-status': {
    path: '/ddp-dataout-status',
    displayName: 'DDP Dataout Status',
  },
  metabase: {
    path: '/metabase',
    displayName: 'Metabase Reports',
  },
  tasks: {
    path: '/tasks',
    displayName: 'Activity List',
  },
  'tasks-push-status': {
    path: '/tasks/tasks-push-status',
    displayName: 'Task Push Status',
  },
  achievement: {
    path: '/:module/achievement-upload',
    displayName: 'Achievement',
  },
  templates: {
    path: '/customize/templates',
    displayName: 'TEMPLATES',
  },
  modules: {
    path: '/customize/modules',
    displayName: 'MODULE_SETTINGS',
  },
  'global-settings': {
    path: '/customize/global-settings',
    displayName: 'GLOBAL_SETTINGS',
  },
  'release-management': {
    path: '/customize/release-management',
    displayName: 'RELEASE_MANAGEMENT',
  },
  'ddp-upload-status': {
    path: '/ddp-upload-status',
    displayName: 'DDP Upload Jobs',
  },
  bi: {
    path: '/bi',
    displayName: 'bi',
  },
  'tableau-standard-reports': {
    path: '/tableau-standard-reports',
    displayName: 'RELEASE_MANAGEMENT',
  },
  'tableau-custom-reports': {
    path: '/tableau-custom-reports',
    displayName: 'RELEASE_MANAGEMENT',
  },
  'bi-edit': {
    path: '/bi-edit',
    displayName: 'RELEASE_MANAGEMENT',
  },
  'goal-bulk-upload': {
    path: '/goal_management/bulk-upload',
    displayName: 'BULK_UPLOAD',
  },
  'activities-bulk-upload': {
    path: '/tasks/bulk-upload',
    displayName: 'BULK_UPLOAD_ACTIVITIES_TITLE',
  },
  'wfh-board': {
    path: '/wfh-board',
    displayName: 'WFH',
  },
  'allocation-hdfc': {
    path: '/allocation-hdfc',
    displayName: 'Allocation',
  },
  'home-page': {
    path: '/:module/dashboard',
    displayName: 'Home Page',
  },
  dashboard: {
    path: '/dashboard',
    displayName: 'Dashboard',
  },
  onboard: {
    path: '/on-board-branches',
    displayName: 'On Board',
  },
  resource_center_links: {
    path: '/resource/links',
    displayName: 'Links',
  },
  ms_teams_result: {
    path: '/ms-team-result',
    displayName: 'Dashboard',
  },
  approvals: {
    path: '/approvals',
    displayName: 'Approvals',
  },
  'client-health-dashboard': {
    path: '/client-health-dashboard',
    displayName: 'CLIENT_HEALTH_DASHBOARD',
  },
  'cypress-test': {
    path: '/test',
    displayName: 'CYPRESS_TEST',
  },
  'manage-groups': {
    path: '/manage-groups',
    displayName: 'MANAGE_GROUPS',
  },
  broadcast: {
    path: '/broadcast',
    displayName: 'Broadcast',
  },
  waiver_matrix: {
    path: '/approval-matrix',
    displayName: 'WAIVER_MATRIX',
  },
  cover: {
    path: '/:module/cover',
    displayName: 'Cover',
  },
  leads: {
    path: '/:module/leads',
    displayName: 'Leads List',
  },
  'bo-list': {
    path: '/:module/bo-list',
    displayName: 'Campaign List',
  },
  lessons: {
    path: '/:module/lessons',
    displayName: 'Lessons',
  },
  assessments: {
    path: '/:module/assessment',
    displayName: 'Assessments',
  },
  course: {
    path: '/:module/course',
    displayName: 'Course',
  },
  'superset-dashboard': {
    path: '/dashboards',
    displayName: 'SUPERSET_DASHBOARD',
  },
};

export const sidebarGroupings = {
  [MENU_GROUP_TYPES.WORKSPACE]: [
    'home-page',
    'tasks',
    'manage-users',
    'common',
    'performance',
  ],
  [MENU_GROUP_TYPES.DISTRIBUTION]: ['PARTNER', 'leads', 'LEAD'],
  [MENU_GROUP_TYPES.COLLECTIONS]: ['revyre'],
  [MENU_GROUP_TYPES.UTILITY]: [
    'goal-bulk-upload',
    'masters',
    'others',
    'reports',
  ],
};

export const SIDEBAR_GROUP_ORDER = [
  MENU_GROUP_TYPES.WORKSPACE,
  MENU_GROUP_TYPES.SALES,
  MENU_GROUP_TYPES.COLLECTIONS,
  MENU_GROUP_TYPES.DISTRIBUTION,
  MENU_GROUP_TYPES.UTILITY,
  MENU_GROUP_TYPES.MORE,
];

export const subMenuMap = {
  '/revyre/ddp-status/details/:jobId': '/workflow-ddp-status/list',
  '/revyre/:domainType/details/:entityId': '/revyre/:domainType',
  '/revyre/:domainType/:workflowType/details/:entityId':
    '/revyre/:domainType/:workflowType',
};
